import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, Check, Loader2, Radio, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { CustomEmojiPlayer } from '@/components/ui/CustomEmojiPlayer';
import { apiFetch } from '@/utils/api';

const CHANNEL_USERNAME = 'KiteMarketplace';
const CHANNEL_URL = `https://t.me/${CHANNEL_USERNAME}`;
const JOINED_KEY = 'kite_mkt_joined';
const POLL_MS = 6000;
/** Right after the user taps "Join" we hammer the check so the gate closes fast. */
const POLL_WAITING_MS = 2500;
const FAST_WINDOW_MS = 90 * 1000;

type Phase = 'boot' | 'waiting' | 'joined' | 'idle';

const readJoined = () => {
    try {
        return localStorage.getItem(JOINED_KEY) === '1';
    } catch {
        return false;
    }
};

const markJoined = () => {
    try {
        localStorage.setItem(JOINED_KEY, '1');
    } catch {}
};

/** After pressing Join the first check should land almost immediately. */
const fastDelay = (joinedAt: number) =>
    joinedAt > 0 && Date.now() - joinedAt < FAST_WINDOW_MS ? 600 : POLL_MS;

const openTelegram = (url: string) => {
    // @ts-ignore
    const wa = window.Telegram?.WebApp;
    if (wa?.openTelegramLink) wa.openTelegramLink(url);
    else window.open(url, '_blank', '_blank');
};

interface ChannelSubscribeModalProps {
    /** Only run the gate once the session is identified. */
    enabled: boolean;
}

/**
 * Mandatory "join the marketing channel" gate.
 *
 * Fail-safe by design: the modal is shown ONLY when the server explicitly
 * answers `{ verifiable: true, subscribed: false }`. Any network error, 404,
 * 401/403 or malformed payload leaves the user alone — a broken check must
 * never lock somebody out of the app.
 */
const ChannelSubscribeModal: React.FC<ChannelSubscribeModalProps> = ({ enabled }) => {
    const { t } = useTranslation('common');
    const { impactOccurred, notificationOccurred } = useHapticFeedback();

    const [visible, setVisible] = useState(false);
    const [phase, setPhase] = useState<Phase>('boot');
    const [checking, setChecking] = useState(false);
    const inFlight = useRef(false);
    const joinedAt = useRef(0);

    /**
     * @param announce haptic + toast when the subscription is confirmed
     * @param fresh    bypass the server cache (used whenever the user comes back
     *                 from Telegram, so joining closes the gate immediately)
     */
    const check = useCallback(
        async (announce = false, fresh = false) => {
            if (inFlight.current) return;
            inFlight.current = true;
            setChecking(true);
            try {
                const res = await apiFetch(
                    `/api/channel/subscribe-status?username=${CHANNEL_USERNAME}${fresh ? '&fresh=1' : ''}`
                );
                if (!res.ok) {
                    console.warn('[channelGate] status endpoint returned', res.status, '— gate skipped');
                    setVisible(false);
                    return;
                }
                const data = await res.json();

                if (data?.success && data?.verifiable && data?.subscribed === true) {
                    markJoined();
                    setVisible(false);
                    setPhase('joined');
                    if (announce) {
                        notificationOccurred('success');
                        impactOccurred('medium');
                    }
                    return;
                }

                if (data?.success && data?.verifiable && data?.subscribed === false) {
                    setVisible(true);
                    if (joinedAt.current === 0) setPhase((p) => (p === 'joined' ? 'joined' : p));
                    return;
                }

                // Bot cannot verify membership (not an admin / offline) → do not gate.
                console.warn('[channelGate] membership not verifiable:', data?.reason);
                setVisible(false);
            } catch (e) {
                console.warn('[channelGate] check failed — gate skipped', e);
                setVisible(false);
            } finally {
                inFlight.current = false;
                setChecking(false);
            }
        },
        [impactOccurred, notificationOccurred]
    );

    // First check as soon as the session is ready
    useEffect(() => {
        if (!enabled) return;
        setPhase('boot');
        check(false);
    }, [enabled, check]);

    // While the gate is up: verify the instant the user comes back, then keep polling.
    useEffect(() => {
        if (!visible || !enabled) return;

        const onReturn = () => {
            if (document.visibilityState === 'visible') check(true, true);
        };
        document.addEventListener('visibilitychange', onReturn);
        window.addEventListener('focus', onReturn);

        // Inside Telegram the webview focus event is unreliable — use its own event.
        // @ts-ignore
        const wa = window.Telegram?.WebApp as any;
        const onTelegramVisibility = () => {
            if (wa?.isActive !== false) check(true, true);
        };
        if (typeof wa?.onEvent === 'function') {
            try {
                wa.onEvent('visibilityChanged', onTelegramVisibility);
            } catch {}
        }

        let timer: ReturnType<typeof setTimeout>;
        const tick = async () => {
            const fast = joinedAt.current > 0 && Date.now() - joinedAt.current < FAST_WINDOW_MS;
            if (document.visibilityState === 'visible') await check(false, fast);
            timer = setTimeout(tick, fast ? POLL_WAITING_MS : POLL_MS);
        };
        timer = setTimeout(tick, fastDelay(joinedAt.current));

        return () => {
            document.removeEventListener('visibilitychange', onReturn);
            window.removeEventListener('focus', onReturn);
            if (typeof wa?.offEvent === 'function') {
                try {
                    wa.offEvent('visibilityChanged', onTelegramVisibility);
                } catch {}
            }
            clearTimeout(timer);
        };
    }, [visible, enabled, check]);

    const handleJoin = () => {
        impactOccurred('light');
        joinedAt.current = Date.now();
        setPhase('waiting');
        openTelegram(CHANNEL_URL);
    };

    if (!enabled || !visible) return null;

    const benefits = [
        { icon: Radio, text: t('joinBenefitDrops', 'Drops and announcements before anyone else') },
        { icon: Bell, text: t('joinBenefitEarly', 'Early access to new features and skins') },
        { icon: ShieldCheck, text: t('joinBenefitSupport', 'News and support in one place') },
    ];

    return (
        <div className="kmg-veil fixed inset-0 z-[130] flex items-end sm:items-center justify-center overflow-y-auto px-4 sm:py-8">
            <style>{`
                @keyframes kmgPop { from { transform: translateY(26px) scale(.95); opacity: 0 } to { transform: none; opacity: 1 } }
                @keyframes kmgSpin { to { transform: rotate(360deg) } }
                @keyframes kmgFloat { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-4px) } }
                @keyframes kmgSheen { 0% { transform: translateX(-130%) } 55%,100% { transform: translateX(230%) } }
                @keyframes kmgPulse { 0%,100% { opacity: .55 } 50% { opacity: .9 } }
                .kmg-veil { background: rgba(5,6,14,.8); backdrop-filter: blur(22px) saturate(140%); -webkit-backdrop-filter: blur(22px) saturate(140%); }
                .kmg-in { animation: kmgPop .44s cubic-bezier(.22,1,.36,1); }
                .kmg-panel {
                    border-radius: 30px;
                    border: 1px solid rgba(255,255,255,.14);
                    background: rgba(14,16,32,.74);
                    box-shadow: 0 40px 120px -20px rgba(0,0,0,.9);
                    backdrop-filter: blur(42px) saturate(180%);
                    -webkit-backdrop-filter: blur(42px) saturate(180%);
                    animation: kmgPop .44s cubic-bezier(.22,1,.36,1) .04s both;
                }
                .kmg-ring {
                    padding: 1px; border-radius: 30px;
                    background: rgba(10,132,255,.55);
                    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
                    -webkit-mask-composite: xor; mask-composite: exclude;
                }
                .kmg-rim { box-shadow: inset 0 0 0 1px rgba(255,255,255,.07), inset 0 1.5px 0 0 rgba(255,255,255,.24), inset 0 -14px 26px -20px rgba(120,180,255,.5); }
                .kmg-liquid {
                    position: absolute; left: 50%; top: -40px; width: 170px; height: 130px;
                    transform: translateX(-50%);
                    background: radial-gradient(closest-side, rgba(10,132,255,.5), rgba(10,132,255,.16) 58%, transparent 78%);
                    filter: url(#kmg-refract); pointer-events: none;
                }
                .kmg-halo { background: #0A84FF; filter: blur(16px); opacity: .5; animation: kmgPulse 3s ease-in-out infinite; }
                .kmg-emoji { animation: kmgFloat 3.4s ease-in-out infinite; }
                .kmg-dot { animation: kmgPulse 2.2s ease-in-out infinite; }
                .kmg-title { color: #ffffff; text-shadow: 0 2px 18px rgba(10,132,255,.35); }
                .kmg-cta { background: #0A84FF; box-shadow: 0 14px 32px -14px rgba(10,132,255,.95), inset 0 1px 0 rgba(255,255,255,.3); }
                .kmg-sheen { position: absolute; inset: 0; background: linear-gradient(105deg, transparent 36%, rgba(255,255,255,.22) 50%, transparent 64%); transform: translateX(-130%); animation: kmgSheen 4.2s ease-in-out infinite; pointer-events: none; }
            `}</style>

            {/* Liquid-glass refraction (same technique as the bottom tabbar) */}
            <svg className="pointer-events-none absolute h-0 w-0" aria-hidden="true">
                <filter id="kmg-refract" x="-25%" y="-25%" width="150%" height="150%">
                    <feTurbulence type="fractalNoise" baseFrequency="0.009 0.012" numOctaves="2" seed="7" result="n" />
                    <feGaussianBlur in="n" stdDeviation="2.4" result="b" />
                    <feDisplacementMap in="SourceGraphic" in2="b" scale="46" xChannelSelector="R" yChannelSelector="G" />
                </filter>
            </svg>

            <div className="kmg-in relative w-full sm:max-w-[400px] my-auto">
                {/* Aurora bloom behind the panel */}
                <div className="pointer-events-none absolute -inset-10 -z-10">
                    <div className="absolute left-1/2 top-0 h-52 w-52 -translate-x-1/2 rounded-full bg-[#0A84FF]/30 blur-[70px]" />
                    <div className="absolute left-4 top-20 h-40 w-40 rounded-full bg-[#0A84FF]/16 blur-[70px]" />
                    <div className="absolute right-2 top-24 h-40 w-40 rounded-full bg-[#0A84FF]/12 blur-[70px]" />
                </div>

                <div className="kmg-panel relative overflow-hidden max-h-[92vh]">
                    {/* Top specular sheen */}
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-white/[0.14] via-white/[0.04] to-transparent" />
                    {/* Animated conic hairline */}
                    <div className="pointer-events-none absolute -inset-[1px] rounded-[30px] kmg-ring" />
                    {/* Refracted rim */}
                    <div className="pointer-events-none absolute inset-0 rounded-[30px] kmg-rim" />

                    <div className="relative px-5 pb-5 pt-6 text-center">
                        {/* Hero */}
                        <div className="relative mx-auto h-[76px] w-[76px]">
                            <div className="kmg-liquid" />
                            <div className="absolute inset-0 rounded-[24px] kmg-halo" />
                            <div className="absolute inset-[3px] rounded-[21px] border border-white/[0.16] bg-white/[0.06] backdrop-blur-xl flex items-center justify-center">
                                <div className="kmg-emoji">
                                    <CustomEmojiPlayer emojiId="Numeo8" size={52} />
                                </div>
                            </div>
                        </div>

                        <h3
                            className="mt-3.5 text-[19px] font-black leading-tight kmg-title"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                            {t('joinMarketingTitle', 'Join Kite Marketing')}
                        </h3>
                        <p className="mx-auto mt-1.5 max-w-[300px] text-[12.5px] leading-relaxed text-white/60 font-semibold">
                            {t('joinMarketingSubtitle', 'Subscribe to our marketing channel to never miss a drop.')}
                        </p>

                        {/* Benefits */}
                        <div className="mt-4 space-y-1.5">
                            {benefits.map(({ icon: Icon, text }) => (
                                <div
                                    key={text}
                                    className="flex items-center gap-2.5 rounded-[14px] border border-white/[0.07] bg-white/[0.045] px-3 py-2.5 text-left backdrop-blur-sm"
                                >
                                    <span
                                        className="grid h-7 w-7 shrink-0 place-items-center rounded-[10px] bg-[#0A84FF] shadow-[0_6px_16px_-6px_rgba(10,132,255,0.95)]"
                                    >
                                        <Icon size={13} className="text-white" strokeWidth={2.5} />
                                    </span>
                                    <p className="text-[12.5px] font-semibold leading-snug text-white/80">{text}</p>
                                </div>
                            ))}
                        </div>

                        {/* Channel handle */}
                        <div className="mt-3.5 inline-flex items-center gap-2 rounded-full border border-white/[0.09] bg-white/[0.05] px-3 py-1 backdrop-blur-sm">
                            <span className="kmg-dot h-1.5 w-1.5 rounded-full bg-[#30D158] shadow-[0_0_10px_2px_rgba(48,209,88,0.75)]" />
                            <span className="portals-numeric text-[11.5px] font-bold tracking-wide text-white/70">@{CHANNEL_USERNAME}</span>
                        </div>

                        {/* Actions */}
                        <div className="mt-4 space-y-2">
                            <button
                                type="button"
                                onClick={handleJoin}
                                className="kmg-cta group relative flex h-[48px] w-full items-center justify-center gap-2 overflow-hidden rounded-2xl text-[14px] font-extrabold text-white active:scale-[0.98] transition-transform"
                            >
                                <span className="relative z-10 flex items-center gap-2">
                                    {phase === 'waiting' ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" />
                                            {t('waitingForJoin', 'Waiting for you to join…')}
                                        </>
                                    ) : (
                                        t('joinChannelNow', 'Join Channel')
                                    )}
                                </span>
                                <span className="kmg-sheen" />
                            </button>

                            <button
                                type="button"
                                disabled={checking}
                                onClick={() => {
                                    impactOccurred('light');
                                    check(true, true);
                                }}
                                className="h-[42px] w-full rounded-2xl border border-white/[0.1] bg-white/[0.05] text-[12.5px] font-bold text-white/65 backdrop-blur-sm active:scale-[0.98] transition-transform disabled:opacity-60"
                            >
                                {checking ? (
                                    <span className="inline-flex items-center gap-2">
                                        <Loader2 size={13} className="animate-spin" />
                                        {t('checkingStatus', 'Checking your subscription…')}
                                    </span>
                                ) : (
                                    t('iJoined', 'I joined — check again')
                                )}
                            </button>
                        </div>

                        <p className="mt-3 flex items-center justify-center gap-1.5 text-[10.5px] font-semibold text-white/35">
                            <Check size={11} />
                            {t('gateAutoNote', 'This window closes automatically once you subscribe.')}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChannelSubscribeModal;
export { CHANNEL_URL, CHANNEL_USERNAME };
